/**
 * A `/game/…` page declares the ONE sport it renders, from its route param,
 * and — once its rows have landed — the ONE event, by the wire keys those rows
 * carry (consumer feed filters).
 *
 * Shared by GameDetail and its /sources sibling — Danielle R10 DNL-010: the
 * fallback treats every /game/* path as sport-scoped, so every page mounted
 * under it must declare, or it silently asks for every sport. The legacy
 * sport-less route shape (no `:sport` segment) falls back to all.
 *
 * **Two-phase, by design** (hydra 2026-09-04, rule 3): a fresh page has no
 * rows, so it has no wire keys and declares its sport alone; the games axis
 * joins the declaration once the rows land. The axes AND, and the `filterAck`
 * plus its replacement snapshot make the narrowing atomic, so there is no dark
 * window between the two phases — only a briefly wider one.
 *
 * The keys MUST come from the feed. Anything composed here would be a
 * different shape than the engines match (see `marketStore.getGameWireKeys`),
 * and an allow-list that matches nothing blanks the page.
 */
import { useMemo } from 'react';
import { useFeedNeeds } from './useFeedNeeds';
import { normalizeSport } from '../stores/marketFrameNormalization';

export function useRouteSportFeedNeeds(
  sourceId: string,
  routeSport: string | undefined,
  /** The wire keys this page's own rows carry. Omit (or pass an empty list)
   *  before the rows land — the sport alone is declared. */
  gameWireKeys?: readonly string[] | null,
): void {
  const sport = routeSport ? normalizeSport(routeSport) : null;
  // Join to a primitive so a fresh array of the same keys is not a new need.
  const gamesKey = gameWireKeys && gameWireKeys.length > 0
    ? [...gameWireKeys].sort().join(' ')
    : '';
  const needs = useMemo(() => {
    if (!sport) return { sports: 'all' as const };
    return gamesKey ? { sports: [sport], games: gamesKey.split(' ') } : { sports: [sport] };
  }, [sport, gamesKey]);
  useFeedNeeds(sourceId, needs);
}
